import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// import mockMenu from 'mock/menuItems.json';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { UserData } from 'types/AuthTypes';
import { getMyAccount } from 'services/authService';
import cloneDeep from 'lodash/cloneDeep';
import { useDispatch } from 'react-redux';
import { setIsLoadMenuSuccess } from 'slices/common/menu/reducer';
import { appendPathToClientUrl } from 'services/helpers/resolveApiBaseUrl';

export interface MenuItem {
  id?: string;
  label: string;
  icon?: string;
  link?: string;
  stateVariables?: boolean;
  click?: (e: any) => void;
  sub_items?: MenuItem[];
  child_items?: MenuItem[];
  is_header?: boolean;
  is_child_item?: boolean;
  parent_id?: string;
}

type IconKey = keyof typeof MapIcons;

const MapIcons = {
  dashboard: 'bx bx-home',
  sales: 'las la-shopping-cart',
  delivery: 'las la-shipping-fast',
  items: 'las la-cubes',
  purchase: 'las la-file-alt',
  promotions: 'las la-gift',
  reports: 'bx bx-bar-chart',
  contact: 'bx bx-group',
  config: 'bx bx-cog',
};

const getIcon = (iconKey: IconKey): string => {
  return MapIcons[iconKey];
};

const Navdata = () => {
  const history = useNavigate();
  const dispatch = useDispatch();

  const commonState = useSelector((state: RootState) => state.Common);
  const { rawMenu, isLoadMenuSuccess } = commonState.menu.fetchUserMenu;
  // State to manage which main items and sub-items are expanded
  const [menuStates, setMenuStates] = useState<{ [key: string]: boolean }>({
    isDashboard: false,
    isSales: false,
    isDelivery: false,
    isItems: false,
    isPurchase: false,
    isPromotion: false,
    isReports: false,
    isContact: false,
    isConfig: false,
    isPoints: false,
  });

  // State to manage expanded state for each sub-item with child items
  const [expandedSubItems, setExpandedSubItems] = useState<{
    [key: string]: boolean;
  }>({});

  const [iscurrentState, setIscurrentState] = useState('Dashboard');

  const toggleMenuState = useCallback((key: string) => {
    setMenuStates((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const toggleSubItemState = (key: string) => {
    setExpandedSubItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const generateMenuItems = useCallback(
    (apiData: MenuItem[]) => {
      return apiData.map((item) => {
        const label = item.label.replace(/\s+/g, '');
        const stateVariableName = `is${label}`;

        if (item.id && !item.icon) {
          item.icon = getIcon(item.id as IconKey);
        }

        item.click =
          item.click ||
          ((e: any) => {
            e.preventDefault();
            toggleMenuState(stateVariableName);
            setIscurrentState(item.label);
          });

        item.stateVariables = menuStates[stateVariableName] || false;

        if (item.sub_items && item.sub_items.length > 0) {
          item.sub_items = generateMenuItems(item.sub_items);

          item.sub_items.forEach((subItem) => {
            subItem.click = (e: any) => {
              e.preventDefault();
              toggleSubItemState(subItem.label);
            };
            subItem.stateVariables = expandedSubItems[subItem.label] || false;

            if (subItem.sub_items && subItem.sub_items.length > 0) {
              subItem.child_items = generateMenuItems(subItem.sub_items);
              subItem.is_child_item = true;
            }
          });
        } else if (item.sub_items && item.sub_items.length === 0) {
          delete item.sub_items;
        }

        return item;
      });
    },
    [expandedSubItems, menuStates, toggleMenuState]
  );

  useEffect(() => {
    setMenuStates((prev) =>
      Object.keys(prev).reduce((acc, key) => {
        acc[key] = `is${iscurrentState}` === key;
        return acc;
      }, {} as { [key: string]: boolean })
    );

    if (iscurrentState === 'Widgets') {
      history('/widgets');
      document.body.classList.add('twocolumn-panel');
    } else {
      document.body.classList.remove('twocolumn-panel');
    }
  }, [iscurrentState, history]);

  const [menuItems, setMenuItems] = useState<any>([]);

  // const fetchMenuItems = useCallback(async () => {
  //   // let menuData: MenuItem[] = [];
  //   let menuData: MenuItem[] = rawMenu;

  //   try {
  //     // if (process.env.NODE_ENV === 'development') {
  //     //   menuData = mockMenu.menu_data;
  //     // } else {
  //     //   const myAccount: UserData = await getMyAccount();
  //     //   if (myAccount?.menu_data) {
  //     //     menuData = myAccount.menu_data;
  //     //   }
  //     // }
  //     if (rawMenu.length === 0) {
  //       const myAccount: UserData = await getMyAccount();
  //       if (myAccount?.menu_data) {
  //         menuData = myAccount.menu_data;
  //       }
  //       console.log('Using menuData from getMyAccount');
  //     } else {
  //       console.log('Using rawMenu from Redux state');
  //     }

  //     const parsedMenu: MenuItem[] = menuData.length
  //       ? menuData
  //       : [
  //           {
  //             id: 'dashboard',
  //             label: 'Dashboard',
  //             link: `${process.env.REACT_APP_OLD_PAGE}/home`,
  //             sub_items: [],
  //           },
  //         ];

  //     setMenuItems(generateMenuItems(parsedMenu));
  //   } catch (error) {
  //     console.error('Error fetching menu items:', error);
  //   }
  // }, [generateMenuItems, rawMenu]);
  // useEffect(() => {
  //   fetchMenuItems();
  // }, [fetchMenuItems]);

  const getMenu = useCallback(async (): Promise<MenuItem[]> => {
    if (rawMenu.length > 0) {
      return rawMenu;
    }
    if (isLoadMenuSuccess) return [];

    try {
      const myAccount: UserData = await getMyAccount();
      dispatch(setIsLoadMenuSuccess(true));

      if (myAccount?.menu_data?.length) {
        return myAccount.menu_data;
      }

      return [
        {
          id: 'dashboard',
          label: 'Dashboard',
          link: appendPathToClientUrl(process.env.REACT_APP_OLD_PAGE, 'home'),
          sub_items: [],
        },
      ];
    } catch (error) {
      console.error('Error fetching menu data:', error);
      return [];
    }
  }, [rawMenu, dispatch, isLoadMenuSuccess]);

  const setNewGenerateMenu = useCallback(async () => {
    const menu = await getMenu();
    const clonedMenu = cloneDeep(menu); // prevent mutation on frozen object
    const generated = generateMenuItems(clonedMenu);
    // const generated = generateMenuItems(menu);
    setMenuItems(generated);
  }, [getMenu, generateMenuItems]);

  useEffect(() => {
    setNewGenerateMenu();
  }, [setNewGenerateMenu]);

  return <React.Fragment>{menuItems}</React.Fragment>;
};

export default Navdata;
